import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import MESSAGES from '../../../../common/data/constants/messages';
import { ITraining } from '../../../../common/data/dtos/training';
import Button from '../../../../components/bootstrap/Button';
import Card, { CardFooterRight, CardTabItem } from '../../../../components/bootstrap/Card';
import FormGroup from '../../../../components/bootstrap/forms/FormGroup';
import Input from '../../../../components/bootstrap/forms/Input';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '../../../../components/bootstrap/Modal';
import Spinner from '../../../../components/bootstrap/Spinner';
import { showSuccess, showWarning } from '../../../../components/extras/Notifications';
import trainingService from '../../../../services/training-service';

type TrainingFormValues = {
    id?: string;
    name: string;
    validity: number;
};

interface Props {
    isModalOpened: boolean;
    selectedTraining: ITraining | null;
    onModalClose: () => void;
    onAfterSubmit: () => void;
}

const TrainingModal = ({ isModalOpened, selectedTraining, onModalClose, onAfterSubmit }: Props) => {
    const formik = useFormik({
        initialValues: {
            name: '',
            validity: 0,
        },

        validate: (values) => {
            const errors: {
                name?: string;
            } = {};

            if (!values.name) errors.name = MESSAGES.VALIDATION.REQUIRED_FIELD;

            return errors;
        },

        onSubmit: (values, { resetForm }) => {
            onFormSubmit(values);
            resetForm();
        },
    });

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    useEffect(() => {
        resetForm();

        if (selectedTraining) setFieldValues(selectedTraining);
    }, [isModalOpened]);

    const onFormSubmit = async (values: TrainingFormValues) => {
        if (selectedTraining) {
            setIsSubmitting(true);

            const response = await trainingService.updateTraining({
                id: selectedTraining.id,
                name: values.name,
                validity: values.validity,
                rowVersion: selectedTraining.rowVersion,
                status: selectedTraining.status,
            });

            setIsSubmitting(false);

            if (response.error) {
                showWarning(MESSAGES.ACTION.EDIT_ERROR);
                return;
            }

            showSuccess(MESSAGES.ACTION.EDITED);
        } else {
            setIsSubmitting(true);

            const response = await trainingService.createTraining({
                name: values.name,
                validity: values.validity,
            });

            setIsSubmitting(false);

            if (response.error) {
                showWarning(MESSAGES.ACTION.SAVE_ERROR);
                return;
            }

            showSuccess(MESSAGES.ACTION.SAVED);
        }

        resetForm();
        onAfterSubmit();
    };

    const onClose = () => {
        onModalClose();
    };

    const resetForm = () => {
        formik.resetForm({
            values: {
                name: '',
                validity: 0,
            },
        });
    };

    const setFieldValues = (values: ITraining) => {
        formik.setFieldValue('name', values.name || '');
        formik.setFieldValue('validity', values.validity || 0);
    };

    return (
        <Modal isOpen={isModalOpened} setIsOpen={onClose} size="lg" isCentered isStaticBackdrop>
            <ModalHeader setIsOpen={onClose}>
                <ModalTitle id="training-modal">Manutenção de treinamentos</ModalTitle>
            </ModalHeader>
            <ModalBody>
                <Card hasTab tabButtonColor="primary">
                    <CardTabItem id="tab-item-1" title="Dados de treinamentos" icon="School     ">
                        <div className="row g-4">
                            <div className="col-lg-12 col-md-12 col-sm-12">
                                <FormGroup id="name" label="Nome" isFloating>
                                    <Input
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.name}
                                        isValid={formik.isValid}
                                        isTouched={formik.touched.name}
                                        invalidFeedback={formik.errors.name}
                                        validFeedback=""
                                    />
                                </FormGroup>
                            </div>

                            <div className="col-lg-12 col-md-12 col-sm-12">
                                <FormGroup id="validity" label="Validade (Dias)" isFloating>
                                    <Input onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.validity} />
                                </FormGroup>
                            </div>
                        </div>
                    </CardTabItem>
                </Card>
            </ModalBody>
            <ModalFooter className="bg-transparent">
                <CardFooterRight>
                    <Button color="success" className="w-100" onClick={formik.handleSubmit} isDisable={isSubmitting}>
                        {isSubmitting && <Spinner isSmall inButton isGrow />}
                        Salvar
                    </Button>
                </CardFooterRight>
            </ModalFooter>
        </Modal>
    );
};

export default TrainingModal;
